// WS-75 life: round screen-facing sprites sized in pixels (WebGPU draws points one pixel wide). One instanced quad per
// point: material = a PointsNodeMaterial drawn by a Sprite (three.js's sprite path: sizeNode in pixels, sizeAttenuation =
// size x half the canvas height / distance, i.e. `size * scale / -mv.z` with main.js's scale = height x pixel ratio / 2).
// uv() runs 0..1 over the quad.
import * as THREE from 'three/webgpu';
import { instancedBufferAttribute, luminance, vec4 } from 'three/tsl';

/**
 * points: an InstancedBufferAttribute (itemSize 3), one sprite each, or a node for the centre (then pass count).
 * Returns the Sprite; sprite.userData.points = the attribute (rewrite it and set needsUpdate to move them).
 */
export function pointSprites(points, material, count = points.count) {
  material.positionNode ||= instancedBufferAttribute(points);
  material.alphaToCoverage = false; // (a PointsNodeMaterial turns it on by default: soft edges would dither under MSAA)
  const s = new THREE.Sprite(material);
  s.count = count;
  s.frustumCulled = false;
  s.raycast = () => {}; // (a Sprite's raycast needs raycaster.camera; these were never picked)
  if (points?.isBufferAttribute) s.userData.points = points;
  return s;
}

/** a PointsNodeMaterial for additive / soft points (unlit, no fog, no depth write) */
export function spriteMaterial(params = {}) {
  return new THREE.PointsNodeMaterial({ transparent: true, depthWrite: false, fog: false, sizeAttenuation: true, ...params });
}

// The glow round a lamp, one look on every map (owner 2026-11-02: a hard ring and a cut that came and went with the
// angle). A soft veil the colour of the light: each pixel fades towards it, so the disc has no edge of its own and the
// picture under it stays. Any pixel below 0.85 stays below it, under the night bloom threshold (0.92, main.js): the
// disc never crosses into the bloom over a lamp's own lit pool (the sheet of light of 2026-11-01). Never a max blend
// (where the glow met the picture it left that hard ring), never added to the picture (the sheet).
// Drawn after everything on the ground (pools, flat shadows, glass, order 0 to 3) and before the map's marks (29 and up).
/** points as pointSprites; light: the lamp's light at this pixel (rgb node, 0 at the disc's rim). The veil's opacity a = lum / (lum + 0.85), so (light + picture) x (1 - a): faint light passes as it is, 0.85 is where it fades to */
export function glowSprites(points, light, params) {
  const lum = luminance(light), a = lum.div(lum.add(0.85));
  const mat = spriteMaterial({ blending: THREE.CustomBlending, blendSrc: THREE.OneFactor, ...params }); // (blendDst: one minus the alpha, as every material)
  mat.colorNode = vec4(light.mul(a.oneMinus()), a);
  const s = pointSprites(points, mat);
  s.renderOrder = 4;
  return s;
}
